import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AddressMapPicker } from "@/features/customer/components/address-map-picker";
import {
  useCreateCustomerAddress,
  useCustomerAddresses,
  useUpdateCustomerAddress,
} from "@/features/customer/hooks/use-customer-addresses";
import { reverseGeocode } from "@/lib/maps/reverse-geocode";

type FormState = {
  label: string;
  line1: string;
  line2: string;
  city: string;
  country: string;
  notes: string;
  lat: number | null;
  lng: number | null;
  isDefault: boolean;
};

const EMPTY: FormState = {
  label: "",
  line1: "",
  line2: "",
  city: "",
  country: "UY",
  notes: "",
  lat: null,
  lng: null,
  isDefault: false,
};

export default function AddressFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id;
  const isEdit = Boolean(id);

  const addressesQuery = useCustomerAddresses();
  const createMutation = useCreateCustomerAddress();
  const updateMutation = useUpdateCustomerAddress();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(!isEdit);
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    if (!(isEdit && addressesQuery.data && !hydrated)) {
      return;
    }
    const existing = addressesQuery.data.find((address) => address.id === id);
    if (!existing) {
      return;
    }
    setForm({
      label: existing.label,
      line1: existing.line1,
      line2: existing.line2 ?? "",
      city: existing.city ?? "",
      country: existing.country,
      notes: existing.notes ?? "",
      lat: existing.lat == null ? null : Number(existing.lat),
      lng: existing.lng == null ? null : Number(existing.lng),
      isDefault: existing.isDefault,
    });
    setHydrated(true);
  }, [addressesQuery.data, hydrated, id, isEdit]);

  const handleMapChange = async (next: { lat: number; lng: number }) => {
    setForm((prev) => ({ ...prev, lat: next.lat, lng: next.lng }));
    setIsGeocoding(true);
    try {
      const result = await reverseGeocode(next.lat, next.lng);
      setForm((prev) => ({
        ...prev,
        line1: prev.line1 || result.line1 || "",
        line2: prev.line2 || result.line2 || "",
        city: prev.city || result.city || "",
        country: result.country ?? prev.country,
      }));
    } catch {
      // silent — user can type address manually
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.label.trim() || !form.line1.trim()) {
      toast.error(t("addresses.form.requiredError", "Etiqueta y dirección son obligatorias"));
      return;
    }

    if (form.lat == null || form.lng == null) {
      toast.error(t("addresses.form.missingPin", "Ubicá el pin en el mapa antes de guardar"));
      return;
    }

    const input = {
      city: form.city.trim() || null,
      country: form.country.trim() || "UY",
      isDefault: form.isDefault,
      label: form.label.trim(),
      lat: form.lat,
      line1: form.line1.trim(),
      line2: form.line2.trim() || null,
      lng: form.lng,
      notes: form.notes.trim() || null,
    };

    if (isEdit && id) {
      await updateMutation.mutateAsync({
        id,
        input,
      });
      toast.success(t("addresses.form.saveSuccess", "Domicilio guardado"));
      navigate("/account/addresses");
      return;
    }

    await createMutation.mutateAsync(input);
    toast.success(t("addresses.form.saveSuccess", "Domicilio guardado"));
    navigate("/account/addresses");
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEdit && !hydrated) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {isEdit
              ? t("addresses.form.editTitle", "Editar domicilio")
              : t("addresses.form.newTitle", "Nuevo domicilio")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddressMapPicker lat={form.lat} lng={form.lng} onChange={handleMapChange} />
          {isGeocoding ? (
            <p className="text-xs text-muted-foreground">
              {t("addresses.form.geocoding", "Buscando dirección...")}
            </p>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="label">{t("addresses.form.label", "Etiqueta")}</Label>
              <Input
                id="label"
                placeholder={t("addresses.form.labelPlaceholder", "Casa, oficina...")}
                value={form.label}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                maxLength={60}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="country">{t("addresses.form.country", "País (ISO-2)")}</Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    country: e.target.value.toUpperCase(),
                  }));
                }}
                maxLength={2}
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="line1">{t("addresses.form.line1", "Dirección")}</Label>
            <Input
              id="line1"
              value={form.line1}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, line1: e.target.value }));
              }}
              maxLength={200}
              required
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="line2">{t("addresses.form.line2", "Apto / piso (opcional)")}</Label>
              <Input
                id="line2"
                value={form.line2}
                onChange={(e) => setForm((prev) => ({ ...prev, line2: e.target.value }))}
                maxLength={200}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="city">{t("addresses.form.city", "Ciudad")}</Label>
              <Input
                id="city"
                value={form.city}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, city: e.target.value }));
                }}
                maxLength={120}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="notes">
              {t("addresses.form.notes", "Notas para el repartidor (opcional)")}
            </Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              maxLength={500}
              rows={3}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.isDefault}
              onCheckedChange={(value) =>
                setForm((prev) => ({ ...prev, isDefault: value === true }))
              }
            />
            <span>{t("addresses.form.makeDefault", "Marcar como predeterminado")}</span>
          </label>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => navigate("/account/addresses")}>
          {t("common.cancel", "Cancelar")}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("common.save", "Guardar")
          )}
        </Button>
      </div>
    </form>
  );
}
